import React, { useState, useRef } from 'react';
import { Camera, Check } from 'lucide-react';

const AVATAR_COLORS = [
  '#5558FF',
  '#0891B2',
  '#7C3AED',
  '#E11D48',
  '#FAF105',
  '#16A34A',
  '#D97706',
];

const ProfileSetup = ({ onComplete }) => {
  const [step, setStep] = useState(1); // 1 = username, 2 = color/photo
  const [username, setUsername] = useState('');
  const [avatarColor, setAvatarColor] = useState(AVATAR_COLORS[0]);
  const [avatarPhoto, setAvatarPhoto] = useState(null);
  const [usePhoto, setUsePhoto] = useState(false);
  const fileRef = useRef();

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPhoto(reader.result);
      setUsePhoto(true);
    };
    reader.readAsDataURL(file);
  };

  const handleFinish = () => {
    const name = username.trim() || 'User';
    localStorage.setItem('profileName', name);
    localStorage.setItem('profileColor', avatarColor);
    if (usePhoto && avatarPhoto) {
      localStorage.setItem('profilePhoto', avatarPhoto);
    } else {
      localStorage.removeItem('profilePhoto');
    }
    onComplete({ name, color: avatarColor, photo: usePhoto ? avatarPhoto : null });
  };

  const avatarInitial = (username.trim() || 'U')[0].toUpperCase();
  const avatarTextColor = avatarColor === '#FAF105' ? '#1a1a1a' : 'white';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'var(--bg-primary)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '2rem',
      animation: 'fadeIn 0.3s ease-out',
    }}>
      {/* Progress dots */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '2.5rem' }}>
        {[1, 2].map(i => (
          <div key={i} style={{
            width: step >= i ? '28px' : '8px',
            height: '8px',
            borderRadius: '999px',
            background: step >= i ? 'var(--accent-color)' : 'var(--border-color)',
            transition: 'all 0.3s ease',
          }} />
        ))}
      </div>

      {/* Avatar preview */}
      <div style={{
        width: '90px', height: '90px', borderRadius: '50%',
        background: usePhoto ? 'transparent' : avatarColor,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '2.2rem', fontWeight: 700,
        color: avatarTextColor,
        marginBottom: '2rem',
        boxShadow: `0 8px 24px ${usePhoto ? '#00000044' : avatarColor + '66'}`,
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        border: `3px solid ${usePhoto ? 'var(--border-color)' : avatarColor}`,
        flexShrink: 0,
      }}>
        {usePhoto && avatarPhoto
          ? <img src={avatarPhoto} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : avatarInitial
        }
      </div>

      {/* Step 1 — Username */}
      {step === 1 && (
        <div style={{ width: '100%', maxWidth: '320px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>
            What's your name?
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0 0 1.5rem' }}>
            This is how you'll appear in the app
          </p>
          <input
            autoFocus
            type="text"
            value={username}
            onChange={e => setUsername(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && setStep(2)}
            placeholder="Your name…"
            maxLength={24}
            style={{
              width: '100%', padding: '0.9rem 1rem',
              background: 'var(--bg-secondary)',
              border: '1.5px solid var(--border-color)',
              borderRadius: '14px', fontSize: '1.1rem',
              color: 'var(--text-primary)', outline: 'none',
              textAlign: 'center', fontWeight: 600,
              transition: 'border-color 0.2s',
              boxSizing: 'border-box',
            }}
            onFocus={e => e.target.style.borderColor = 'var(--accent-color)'}
            onBlur={e => e.target.style.borderColor = 'var(--border-color)'}
          />
          <button
            onClick={() => setStep(2)}
            style={{
              marginTop: '1rem', width: '100%', padding: '0.9rem',
              background: 'var(--accent-color)', border: 'none',
              borderRadius: '14px', color: 'white',
              fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
            }}
          >
            Continue →
          </button>
        </div>
      )}

      {/* Step 2 — Color + Photo in one grid */}
      {step === 2 && (
        <div style={{ width: '100%', maxWidth: '320px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.5rem' }}>
            Choose your style
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0 0 1.5rem' }}>
            Pick a color or upload a photo
          </p>

          {/* 7 colors + 1 camera button */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '0.75rem', marginBottom: '1.5rem',
          }}>
            {AVATAR_COLORS.map(color => {
              const isActive = !usePhoto && avatarColor === color;
              return (
                <button
                  key={color}
                  onClick={() => { setAvatarColor(color); setUsePhoto(false); }}
                  style={{
                    width: '100%', aspectRatio: '1',
                    borderRadius: '50%', background: color,
                    border: isActive ? '3px solid white' : '3px solid transparent',
                    cursor: 'pointer',
                    outline: isActive ? `3px solid ${color}` : 'none',
                    transition: 'all 0.2s', padding: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: isActive ? `0 4px 14px ${color}88` : 'none',
                  }}
                >
                  {isActive && <Check size={18} color={color === '#FAF105' ? '#1a1a1a' : 'white'} strokeWidth={3} />}
                </button>
              );
            })}

            {/* 8th slot: custom photo picker */}
            <button
              onClick={() => fileRef.current.click()}
              style={{
                width: '100%', aspectRatio: '1',
                borderRadius: '50%',
                background: usePhoto && avatarPhoto ? 'transparent' : 'var(--bg-secondary)',
                border: usePhoto ? '3px solid var(--accent-color)' : '3px solid var(--border-color)',
                cursor: 'pointer', padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
                outline: usePhoto ? `3px solid var(--accent-color)` : 'none',
                boxShadow: usePhoto ? '0 4px 14px rgba(85,88,255,0.4)' : 'none',
                transition: 'all 0.2s',
              }}
            >
              {usePhoto && avatarPhoto
                ? <img src={avatarPhoto} alt="custom" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <Camera size={20} color="var(--text-secondary)" />
              }
            </button>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handlePhotoChange}
          />

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setStep(1)}
              style={{
                flex: 1, padding: '0.9rem',
                background: 'var(--bg-secondary)', border: '1.5px solid var(--border-color)',
                borderRadius: '14px', color: 'var(--text-primary)',
                fontSize: '1rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              ← Back
            </button>
            <button
              onClick={handleFinish}
              style={{
                flex: 2, padding: '0.9rem',
                background: 'var(--accent-color)', border: 'none',
                borderRadius: '14px', color: 'white',
                fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
              }}
            >
              Let's go ✓
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSetup;
